import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  CircularProgress, 
  Paper, 
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Grid,
  Chip,
  IconButton,
  Alert,
  styled,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Upload as UploadIcon,
  Description as DescriptionIcon,
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import WorkflowChat from '../WorkflowChat';
import featureService from '../../../services/featureService';
import useNotification from '../../../hooks/useNotification';
import ReactMarkdown from 'react-markdown';

// Styled components
const StyledAccordion = styled(Accordion)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  boxShadow: '0px 2px 4px rgba(0,0,0,0.05)',
  '&:before': {
    display: 'none', // Remove the default line
  },
  borderRadius: '8px',
  overflow: 'hidden',
  border: '1px solid rgba(0,0,0,0.08)',
}));

const StyledAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
  backgroundColor: 'rgba(0,0,0,0.02)',
  '&.Mui-expanded': {
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },
}));

const UserStories = ({ onClose, existingContent = '', onSaveContent }) => {
  const { projectId } = useParams();
  const { showNotification } = useNotification();
  
  // State for storing user stories and UI state
  const [userStories, setUserStories] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [expandedStory, setExpandedStory] = useState(null);
  const [showChat, setShowChat] = useState(false);
  const [parsedContent, setParsedContent] = useState('');
  const [selectedFeature, setSelectedFeature] = useState(null);
  const [uploadContent, setUploadContent] = useState('');
  
  // Effect to parse existing content if available
  useEffect(() => {
    if (existingContent) {
      setParsedContent(existingContent);
      try {
        // Try to parse the content to extract user stories
        parseUserStories(existingContent);
      } catch (error) {
        console.error('Error parsing existing content:', error);
      }
    }
  }, [existingContent]);
  
  // Load features from the project
  useEffect(() => {
    if (projectId) {
      loadFeatures();
    }
  }, [projectId]);
  
  // Load features from the backend
  const loadFeatures = async () => {
    try {
      setLoading(true);
      const featuresData = await featureService.getProjectFeatures(projectId);
      setFeatures(featuresData);
      
      // Load user stories for each feature
      const storiesPromises = featuresData.map(feature => 
        featureService.getFeatureUserStories(feature._id)
      );
      
      const storiesResults = await Promise.all(storiesPromises);
      
      // Combine all stories with feature info
      const allStories = [];
      featuresData.forEach((feature, index) => {
        const featureStories = storiesResults[index] || [];
        featureStories.forEach(story => {
          allStories.push({
            ...story,
            featureTitle: feature.title
          });
        });
      });
      
      setUserStories(allStories);
      setLoading(false);
    } catch (error) {
      console.error('Error loading features:', error);
      showNotification({
        message: `Error loading features: ${error.message}`,
        type: 'error'
      });
      setLoading(false);
    }
  };
  
  // Parse user stories from markdown content
  const parseUserStories = (content) => {
    try {
      console.log("Parsing user stories from content");
      
      // Regular expression to identify user stories
      const storyRegex = /(?:(?:User Story|As a|US\d{3})[\s\-:]*)([\s\S]*?)(?=(?:User Story|As a|US\d{3})|$)/gi;
      let match;
      let parsedStories = [];
      
      // Iterate through all matches
      while ((match = storyRegex.exec(content)) !== null) {
        const storyContent = match[1].trim();
        
        // Skip if empty content
        if (!storyContent) continue;
        
        // Try to extract title (first line or "As a..." part)
        const titleMatch = storyContent.match(/(.*?)[\r\n]/);
        const asAMatch = storyContent.match(/As a (.*?), I want to (.*?) so that (.*?)[\r\n.]/i);
        
        let title = '';
        let description = storyContent;
        
        if (asAMatch) {
          title = `As a ${asAMatch[1]}, I want to ${asAMatch[2]}`;
          description = storyContent;
        } else if (titleMatch) {
          title = titleMatch[1].trim();
          description = storyContent;
        }
        
        // Extract acceptance criteria if present
        const acceptanceCriteriaMatch = storyContent.match(/Acceptance Criteria:([\s\S]*?)(?=(?:Story Points|Priority|$))/i);
        let acceptanceCriteria = [];
        
        if (acceptanceCriteriaMatch) {
          const criteriaText = acceptanceCriteriaMatch[1].trim();
          acceptanceCriteria = criteriaText
            .split(/[\r\n]+/)
            .map(line => line.replace(/^[\s-]*/, '').trim())
            .filter(line => line.length > 0);
        }
        
        // Extract story points if present
        const storyPointsMatch = storyContent.match(/Story Points:\s*(\d+)/i);
        const storyPoints = storyPointsMatch ? parseInt(storyPointsMatch[1], 10) : 0;
        
        // Extract priority if present
        const priorityMatch = storyContent.match(/Priority:\s*(Must-have|Should-have|Could-have|Won't have)/i);
        const priority = priorityMatch ? priorityMatch[1] : '';
        
        parsedStories.push({
          title,
          description,
          acceptanceCriteria,
          storyPoints,
          priority,
          // Generate a temporary ID
          id: `temp-${parsedStories.length + 1}`
        });
      }
      
      console.log(`Parsed ${parsedStories.length} user stories from content`);
      
      // Update state with parsed stories if found
      if (parsedStories.length > 0) {
        setUserStories(parsedStories);
      }
      
      return parsedStories;
    } catch (error) {
      console.error('Error parsing user stories:', error);
      return [];
    }
  };
  
  // Handle creating a new user story
  const handleCreateUserStory = async (featureId, storyData) => {
    try {
      setLoading(true);
      const newStory = await featureService.createUserStory(featureId, {
        title: storyData.title,
        description: storyData.description,
        acceptanceCriteria: storyData.acceptanceCriteria,
        storyPoints: storyData.storyPoints || 0,
        status: 'pending'
      });
      
      // Add feature info to the new story
      const feature = features.find(f => f._id === featureId);
      const storyWithFeature = {
        ...newStory,
        featureTitle: feature ? feature.title : 'Unknown Feature'
      };
      
      // Update user stories state
      setUserStories([...userStories, storyWithFeature]);
      setLoading(false);
      
      showNotification({
        message: 'User story created successfully',
        type: 'success'
      });
      
      return newStory;
    } catch (error) {
      console.error('Error creating user story:', error);
      showNotification({
        message: `Error creating user story: ${error.message}`,
        type: 'error'
      });
      setLoading(false);
      throw error;
    }
  };
  
  // Handle uploading user stories from text
  const handleUploadStories = () => {
    if (!uploadContent.trim()) {
      showNotification({
        message: 'Please enter user story content to upload',
        type: 'warning'
      });
      return;
    }
    
    try {
      // Parse the uploaded content
      const parsedStories = parseUserStories(uploadContent);
      
      if (parsedStories.length === 0) {
        showNotification({
          message: 'No valid user stories found in the uploaded content',
          type: 'warning'
        });
        return;
      }
      
      // Update the content and close the dialog
      setParsedContent(uploadContent);
      setUserStories(parsedStories);
      setUploadDialogOpen(false);
      
      showNotification({
        message: `Successfully parsed ${parsedStories.length} user stories`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error uploading user stories:', error);
      showNotification({
        message: `Error parsing user stories: ${error.message}`,
        type: 'error'
      });
    }
  };
  
  // Handle saving all user stories
  const handleSaveAllStories = () => {
    // Format all stories into markdown
    let markdown = "# User Stories\n\n";
    
    userStories.forEach((story, index) => {
      const storyId = `US${String(index + 1).padStart(3, '0')}`;
      
      markdown += `## ${storyId}: ${story.title}\n\n`;
      markdown += `${story.description}\n\n`;
      
      if (story.acceptanceCriteria && story.acceptanceCriteria.length > 0) {
        markdown += "**Acceptance Criteria:**\n";
        story.acceptanceCriteria.forEach(criteria => {
          markdown += `- ${criteria}\n`;
        });
        markdown += "\n";
      }
      
      if (story.storyPoints) {
        markdown += `**Story Points:** ${story.storyPoints}\n\n`;
      }
      
      if (story.priority) {
        markdown += `**Priority:** ${story.priority}\n\n`;
      }
      
      markdown += "---\n\n";
    });
    
    // Save the markdown content
    if (onSaveContent) {
      onSaveContent(markdown);
      
      showNotification({
        message: 'User stories saved successfully',
        type: 'success'
      });
    }
  };
  
  // Handle batch upload of user stories to the backend
  const handleBatchUpload = async () => {
    if (!userStories.length) {
      showNotification({
        message: 'No user stories to upload',
        type: 'warning'
      });
      return;
    }
    
    try {
      setLoading(true);
      
      // First, check if we have features available
      if (!features.length) {
        // Try to create a default feature
        const defaultFeature = await featureService.createFeature(projectId, {
          title: 'Default Feature',
          description: 'Default feature for user stories',
          priority: 'medium'
        });
        
        setFeatures([defaultFeature]);
        
        // Associate all stories with this feature
        const storiesData = userStories.map(story => ({
          title: story.title,
          description: story.description,
          acceptanceCriteria: story.acceptanceCriteria || [],
          storyPoints: story.storyPoints || 0,
          status: 'pending',
          featureId: defaultFeature._id
        }));
        
        // Try batch upload if available
        try {
          await featureService.batchUploadUserStories(projectId, storiesData);
        } catch (batchError) {
          console.log('Batch upload not supported, uploading individually');
          
          // Upload stories one by one
          for (const storyData of storiesData) {
            await featureService.createUserStory(defaultFeature._id, storyData);
          }
        }
      } else {
        // If we have multiple features, associate stories with the first feature
        const defaultFeatureId = features[0]._id;
        
        const storiesData = userStories.map(story => ({
          title: story.title,
          description: story.description,
          acceptanceCriteria: story.acceptanceCriteria || [],
          storyPoints: story.storyPoints || 0,
          status: 'pending',
          featureId: defaultFeatureId
        }));
        
        // Try batch upload if available
        try {
          await featureService.batchUploadUserStories(projectId, storiesData);
        } catch (batchError) {
          console.log('Batch upload not supported, uploading individually');
          
          // Upload stories one by one
          for (const storyData of storiesData) {
            await featureService.createUserStory(defaultFeatureId, storyData);
          }
        }
      }
      
      showNotification({
        message: 'User stories uploaded to backend successfully',
        type: 'success'
      });
      
      // Reload features and stories
      await loadFeatures();
      
      setLoading(false);
    } catch (error) {
      console.error('Error batch uploading user stories:', error);
      showNotification({
        message: `Error uploading user stories: ${error.message}`,
        type: 'error'
      });
      setLoading(false);
    }
  };
  
  // Handle generating user stories from PRD
  const handleGenerateFromPRD = () => {
    setShowChat(true);
  };
  
  // Render user story card
  const renderUserStoryCard = (story, index) => {
    return (
      <StyledAccordion 
        key={story.id || index}
        expanded={expandedStory === (story.id || index)}
        onChange={() => setExpandedStory(expandedStory === (story.id || index) ? null : (story.id || index))}
      >
        <StyledAccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
            <Box sx={{ 
              bgcolor: 'primary.main', 
              color: 'white', 
              borderRadius: '4px',
              p: 0.5,
              px: 1,
              mr: 2,
              fontSize: '0.75rem',
              fontWeight: 'bold',
              minWidth: '60px',
              textAlign: 'center'
            }}>
              {story._id ? `US${String(index + 1).padStart(3, '0')}` : `US${String(index + 1).padStart(3, '0')}`}
            </Box>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                {story.title}
              </Typography>
              {story.featureTitle && (
                <Typography variant="body2" color="text.secondary">
                  Feature: {story.featureTitle}
                </Typography>
              )}
            </Box>
            <Box>
              {story.priority && (
                <Chip 
                  size="small" 
                  label={story.priority} 
                  color={
                    story.priority.toLowerCase().includes('must') ? 'error' :
                    story.priority.toLowerCase().includes('should') ? 'warning' :
                    story.priority.toLowerCase().includes('could') ? 'info' : 'default'
                  }
                  sx={{ mr: 1 }}
                />
              )}
              {story.storyPoints > 0 && (
                <Chip 
                  size="small" 
                  label={`${story.storyPoints} pts`} 
                  variant="outlined"
                />
              )}
            </Box>
          </Box>
        </StyledAccordionSummary>
        <AccordionDetails sx={{ p: 3 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                {story.description}
              </Typography>
            </Grid>
            
            {story.acceptanceCriteria && story.acceptanceCriteria.length > 0 && (
              <Grid item xs={12}>
                <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>
                  Acceptance Criteria:
                </Typography>
                <Box component="ul" sx={{ pl: 2 }}>
                  {story.acceptanceCriteria.map((criteria, i) => (
                    <Box component="li" key={i} sx={{ mb: 0.5 }}>
                      <Typography variant="body2">{criteria}</Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            )}
            
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<EditIcon />}
                sx={{ mr: 1 }}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="error"
                startIcon={<DeleteIcon />}
              >
                Delete
              </Button>
            </Grid>
          </Grid>
        </AccordionDetails>
      </StyledAccordion>
    );
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {showChat ? (
        // Show chat interface for generating user stories
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <WorkflowChat
            sectionName="User Stories"
            projectId={projectId}
            existingContent={existingContent}
            onSaveContent={(content) => {
              setParsedContent(content);
              parseUserStories(content);
              setShowChat(false);
              
              if (onSaveContent) {
                onSaveContent(content);
              }
            }}
            placeholder="Describe the user stories you'd like to generate..."
          />
        </Box>
      ) : (
        // Show user stories list view
        <Box sx={{ p: 3, flexGrow: 1, overflow: 'auto' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6">User Stories</Typography>
            <Box>
              <Button 
                variant="contained" 
                startIcon={<DescriptionIcon />}
                onClick={handleGenerateFromPRD}
                sx={{ mr: 1 }}
              >
                Generate from PRD
              </Button>
              <Button 
                variant="outlined" 
                startIcon={<UploadIcon />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload
              </Button>
            </Box>
          </Box>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
              <CircularProgress />
            </Box>
          ) : userStories.length > 0 ? (
            <Box>
              {userStories.map((story, index) => renderUserStoryCard(story, index))}
              
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<UploadIcon />}
                  onClick={handleBatchUpload}
                  sx={{ mr: 2 }}
                  disabled={loading}
                >
                  {loading ? 'Uploading...' : 'Upload to Backend'}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                  onClick={handleSaveAllStories}
                  disabled={loading}
                >
                  Save All Stories
                </Button>
              </Box>
            </Box>
          ) : (
            <Paper 
              sx={{ 
                p: 3, 
                textAlign: 'center',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '200px'
              }}
            >
              <PersonIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
              <Typography variant="h6" color="text.secondary" gutterBottom>
                No User Stories Yet
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: '600px' }}>
                User stories help translate features into specific requirements from a user's perspective.
                Generate them automatically using AI or upload your existing stories.
              </Typography>
              <Box>
                <Button 
                  variant="contained" 
                  startIcon={<DescriptionIcon />}
                  onClick={handleGenerateFromPRD}
                  sx={{ mr: 2 }}
                >
                  Generate with AI
                </Button>
                <Button 
                  variant="outlined" 
                  startIcon={<UploadIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                >
                  Upload Stories
                </Button>
              </Box>
            </Paper>
          )}
        </Box>
      )}
      
      {/* Upload Dialog */}
      {uploadDialogOpen && (
        <Paper
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: '80%',
            maxWidth: '800px',
            p: 4,
            outline: 'none',
            zIndex: 1300,
            boxShadow: 24,
          }}
        >
          <Typography variant="h6" gutterBottom>
            Upload User Stories
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }}>
            Paste your user stories in the text area below. The system will automatically parse and organize them.
          </Typography>
          
          <TextField
            multiline
            rows={12}
            fullWidth
            placeholder="Paste your user stories here..."
            value={uploadContent}
            onChange={(e) => setUploadContent(e.target.value)}
            variant="outlined"
            sx={{ mb: 3 }}
          />
          
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button 
              variant="outlined" 
              onClick={() => setUploadDialogOpen(false)}
              sx={{ mr: 1 }}
            >
              Cancel
            </Button>
            <Button 
              variant="contained"
              onClick={handleUploadStories}
            >
              Process Stories
            </Button>
          </Box>
        </Paper>
      )}
    </Box>
  );
};

export default UserStories;